
import {
    Component,
    Emit,
    Prop,
    PropSync,
    Vue,
    Watch,
} from 'vue-property-decorator';
import Grid from '@/components/event-flow/event-flow-grid/Grid.vue';
import TimelineGrid from '@/components/event-flow/event-flow-grid/TimelineGrid.vue';
import LocationHeader from '@/components/event-flow/event-flow-grid/LocationHeader.vue';
import TopicGrid from '@/components/event-flow/event-flow-grid/TopicGrid.vue';
import GridItem from '@/components/event-flow/event-flow-grid/GridItem.vue';
import { addMinutes, differenceInMinutes } from 'date-fns';
import { differenceInTime, timeFormatOptions } from '@/lib/date-util';
import {
    EventFlowItem,
    EventFlowLocation,
    EventFlowTopic,
    ExtraFunctionFormData,
} from '@/model/event-flow/EventFlow';
import { setFlow } from '@/lib/helper/event-flow-helper';
import cloneDeep from 'lodash.clonedeep';
import { PropType } from 'vue';
import EventFlowStep from '@/model/enums/event-flow/event-flow-step';
import EventFlowLegend from '@/components/event-flow/EventFlowLegend.vue';
import { Responsible } from '@/model/Responsible';

@Component({
    components: {
        Grid,
        TimelineGrid,
        LocationHeader,
        TopicGrid,
        GridItem,
        EventFlowLegend,
    },
})
export default class EventFlowGrid extends Vue {
    private static readonly TOOLTIP_DESCRIPTION_LIMIT = 2000;
    private static readonly GRID_ROW_HEIGHT = 48;
    private static readonly GRID_TOP_OFFSET = 25;
    private static readonly TIME_RULER_REFRESH_INTERVAL = 15000;

    @Prop({ type: Number, default: EventFlowStep.THIRTY_MINUTES })
    readonly step!: number;

    @Prop(Date) readonly startDateTime?: Date;

    @Prop(Date) readonly endDateTime?: Date;

    @Prop({ type: Array, default: () => [] })
    readonly eventFlowItems!: EventFlowItem[];

    @Prop({ default: false, type: Boolean }) readonly isSheet!: boolean;

    @PropSync('extraData', {
        type: Object as PropType<ExtraFunctionFormData>,
        default: () => ({
            showTime: true,
            showBrief: true,
            showLegend: true,
            showTimeline: true,
        }),
    })
    readonly extraFunctionFormData!: ExtraFunctionFormData;

    @Prop({ type: Array, default: () => [] })
    readonly responsibles!: Responsible[];

    eventFlow = setFlow(cloneDeep(this.eventFlowItems));

    movedObject: EventFlowItem | null = null;

    lastPersistedDragItemSignature: string | null = null;

    currentTime = new Date();

    timeRulerIntervalId: number | null = null;

    created(): void {
        this.$root.$on('click', this.click);
        this.$root.$on('updateItem', this.update);
        this.$root.$on('changeMoveItem', this.changeMoveItem);
        this.$root.$on('itemChangedLocal', this.itemChangedLocal);
        this.$root.$on('finishMoveItem', this.finishMoveItem);
    }

    mounted(): void {
        this.updateCurrentTime();
        this.timeRulerIntervalId = window.setInterval(() => {
            this.updateCurrentTime();
        }, EventFlowGrid.TIME_RULER_REFRESH_INTERVAL);
    }

    destroyed(): void {
        this.$root.$off('click', this.click);
        this.$root.$off('updateItem', this.update);
        this.$root.$off('changeMoveItem', this.changeMoveItem);
        this.$root.$off('itemChangedLocal', this.itemChangedLocal);
        this.$root.$off('finishMoveItem', this.finishMoveItem);

        if (this.timeRulerIntervalId !== null) {
            window.clearInterval(this.timeRulerIntervalId);
            this.timeRulerIntervalId = null;
        }
    }

    get eventFlowLocations(): EventFlowLocation[] {
        return this.eventFlow?.eventFlowLocations ?? [];
    }

    get legendResponsibles(): Responsible[] {
        const responsibleIds = new Set(
            this.eventFlowItems
                .map((eventFlowItem) => eventFlowItem.responsible?.id)
                .filter((responsibleId): responsibleId is number => {
                    return (
                        responsibleId !== undefined && responsibleId !== null
                    );
                }),
        );

        return this.responsibles.filter((responsible) =>
            responsibleIds.has(responsible.id),
        );
    }

    get timeValues(): string[] {
        const timeValues = [] as string[];
        if (!this.startDateTime || !this.endDateTime) {
            return timeValues;
        }

        let sTime = this.startDateTime;

        while (this.endDateTime >= sTime) {
            timeValues.push(this.formatDateToTime(sTime));
            sTime = addMinutes(sTime, this.effectiveStep);
        }

        return timeValues;
    }

    get effectiveStep(): number {
        return this.step > 0 ? this.step : EventFlowStep.THIRTY_MINUTES;
    }

    get durationInMinutes(): number {
        if (!this.startDateTime || !this.endDateTime) {
            return 0;
        }

        return Math.max(
            differenceInMinutes(this.endDateTime, this.startDateTime),
            0,
        );
    }

    get minuteRowHeight(): number {
        return EventFlowGrid.GRID_ROW_HEIGHT / this.effectiveStep;
    }

    get showTimeRuler(): boolean {
        if (
            !this.extraFunctionFormData.showTimeline ||
            !this.startDateTime ||
            !this.endDateTime
        ) {
            return false;
        }

        const currentTime = this.currentTime.getTime();

        return (
            currentTime >= this.startDateTime.getTime() &&
            currentTime <= this.endDateTime.getTime()
        );
    }

    get timeRulerTop(): number {
        if (!this.startDateTime || !this.endDateTime) {
            return EventFlowGrid.GRID_TOP_OFFSET;
        }

        const totalDurationInMs =
            this.endDateTime.getTime() - this.startDateTime.getTime();

        if (totalDurationInMs <= 0) {
            return EventFlowGrid.GRID_TOP_OFFSET;
        }

        const elapsedDurationInMs =
            this.currentTime.getTime() - this.startDateTime.getTime();

        const ratio = Math.min(
            Math.max(elapsedDurationInMs / totalDurationInMs, 0),
            1,
        );
        const gridHeight = this.durationInMinutes * this.minuteRowHeight;

        return EventFlowGrid.GRID_TOP_OFFSET + ratio * gridHeight;
    }

    getMinuteStringFromTimeString(timeString: string): string {
        return timeString?.split(':')[1] ?? '';
    }

    showBorder(timeString: string): boolean {
        if (
            this.effectiveStep === EventFlowStep.SIXTY_MINUTES ||
            this.effectiveStep === EventFlowStep.THIRTY_MINUTES
        ) {
            return false;
        }

        const minuteString = this.getMinuteStringFromTimeString(timeString);

        if (
            minuteString === '00' &&
            [
                EventFlowStep.FIFTEEN_MINUTES,
                EventFlowStep.TEN_MINUTES,
                EventFlowStep.FIVE_MINUTES,
                EventFlowStep.ONE_MINUTE,
            ].includes(this.effectiveStep)
        ) {
            return true;
        }

        if (
            minuteString === '30' &&
            [
                EventFlowStep.TEN_MINUTES,
                EventFlowStep.FIVE_MINUTES,
                EventFlowStep.ONE_MINUTE,
            ].includes(this.effectiveStep)
        ) {
            return true;
        }

        if (
            minuteString === '15' &&
            EventFlowStep.ONE_MINUTE === this.effectiveStep
        ) {
            return true;
        }

        return false;
    }

    @Watch('eventFlowItems', { deep: true })
    eventFlowUpdate(): void {
        this.eventFlow = setFlow(cloneDeep(this.eventFlowItems));
    }

    itemChangedLocal(eventFlowItem: EventFlowItem, sortItems = true): void {
        const newLocation = this.findEventFlowLocation(
            eventFlowItem.location?.id,
        );
        const newTopic = this.findEventFlowTopic(
            newLocation,
            eventFlowItem.topic?.id,
        );

        if (this.isMovedObject(eventFlowItem)) {
            const placementChanged = this.updateMovedObject(
                eventFlowItem,
                newTopic,
            );
            if (placementChanged) {
                this.persistDraggedItem(eventFlowItem);
            }
        }

        this.updateTopicItem(newTopic, eventFlowItem, sortItems);
    }

    private findEventFlowLocation(
        locationId?: number,
    ): EventFlowLocation | undefined {
        return this.eventFlow?.eventFlowLocations.find(
            (location) =>
                location.id === (locationId === undefined ? null : locationId),
        );
    }

    private findEventFlowTopic(
        location: EventFlowLocation | undefined,
        topicId?: number,
    ): EventFlowTopic | undefined {
        return location?.eventFlowTopics.find(
            (topic) => topic.id === (topicId === undefined ? null : topicId),
        );
    }

    private isMovedObject(eventFlowItem: EventFlowItem): boolean {
        return (
            this.movedObject !== null &&
            (this.movedObject === eventFlowItem ||
                (this.movedObject.id !== undefined &&
                    this.movedObject.id === eventFlowItem.id))
        );
    }

    private updateMovedObject(
        eventFlowItem: EventFlowItem,
        newTopic: EventFlowTopic | undefined,
    ): boolean {
        if (!this.movedObject) {
            return false;
        }

        this.movedObject.startedAt = eventFlowItem.startedAt;
        this.movedObject.endedAt = eventFlowItem.endedAt;

        if (
            this.movedObject.topic?.id === eventFlowItem.topic?.id &&
            this.movedObject.location?.id === eventFlowItem.location?.id
        ) {
            return false;
        }

        const oldLocation = this.findEventFlowLocation(
            this.movedObject.location?.id,
        );
        const oldTopic = this.findEventFlowTopic(
            oldLocation,
            this.movedObject.topic?.id,
        );

        this.moveItemToTopic(eventFlowItem, oldTopic, newTopic);
        this.movedObject.location = eventFlowItem.location;
        this.movedObject.topic = eventFlowItem.topic;
        return true;
    }

    private moveItemToTopic(
        eventFlowItem: EventFlowItem,
        oldTopic: EventFlowTopic | undefined,
        newTopic: EventFlowTopic | undefined,
    ): void {
        if (!oldTopic) {
            return;
        }

        const oldIndex = oldTopic.items.findIndex(
            (item) => item.id === eventFlowItem.id,
        );
        if (oldIndex < 0) {
            return;
        }

        const oldItems = oldTopic.items.splice(oldIndex, 1);
        if (!newTopic || oldItems.length === 0) {
            return;
        }

        oldItems[0].location = eventFlowItem.location;
        oldItems[0].topic = eventFlowItem.topic;
        oldItems[0].startedAt = eventFlowItem.startedAt;
        oldItems[0].endedAt = eventFlowItem.endedAt;
        newTopic.items.push(oldItems[0]);
    }

    private updateTopicItem(
        topic: EventFlowTopic | undefined,
        eventFlowItem: EventFlowItem,
        sortItems: boolean,
    ): void {
        if (!topic) {
            return;
        }

        const changedItem = topic.items.find(
            (item) => item.id === eventFlowItem.id,
        );
        if (changedItem) {
            changedItem.startedAt = eventFlowItem.startedAt;
            changedItem.endedAt = eventFlowItem.endedAt;
        }

        if (sortItems) {
            topic.items = topic.items.sort(this.compareEventFlowItems);
        }
    }

    private compareEventFlowItems(
        firstItem: EventFlowItem,
        secondItem: EventFlowItem,
    ): number {
        if (firstItem.startedAt.getTime() === secondItem.startedAt.getTime()) {
            return firstItem.endedAt >= secondItem.endedAt ? 1 : -1;
        }

        return firstItem.startedAt > secondItem.startedAt ? 1 : -1;
    }

    @Emit()
    click(item: EventFlowItem): EventFlowItem {
        return item;
    }

    @Emit()
    update(item: EventFlowItem): EventFlowItem {
        const normalizedItem = {
            ...item,
            location: item.location,
            topic: item.topic,
        };

        if (normalizedItem.location?.id == null) {
            normalizedItem.location = undefined;
        }
        if (normalizedItem.topic?.id == null) {
            normalizedItem.topic = undefined;
        }
        return normalizedItem;
    }

    changeMoveItem(eventFlowItem: EventFlowItem | null): void {
        if (eventFlowItem !== null) {
            this.lastPersistedDragItemSignature = null;
        }
        this.movedObject = eventFlowItem;
    }

    finishMoveItem(fallbackItem: EventFlowItem): void {
        const itemToPersist = this.movedObject ?? fallbackItem;
        const itemSignature = this.getDraggedItemSignature(itemToPersist);

        if (
            itemToPersist &&
            itemSignature !== this.lastPersistedDragItemSignature
        ) {
            this.update(itemToPersist);
        }

        this.lastPersistedDragItemSignature = null;
        this.movedObject = null;
    }

    private persistDraggedItem(eventFlowItem: EventFlowItem): void {
        this.lastPersistedDragItemSignature =
            this.getDraggedItemSignature(eventFlowItem);
        this.update(eventFlowItem);
    }

    private getDraggedItemSignature(eventFlowItem: EventFlowItem): string {
        return [
            eventFlowItem.id ?? 'new',
            eventFlowItem.location?.id ?? 'none',
            eventFlowItem.topic?.id ?? 'none',
            eventFlowItem.startedAt.getTime(),
            eventFlowItem.endedAt.getTime(),
        ].join(':');
    }

    createTooltipText(eventFlowItem: EventFlowItem): {
        title: string;
        disabled: boolean;
        html: boolean;
        trigger: string;
        placement: string;
        fallbackPlacement: string[];
        boundary: string;
        boundaryPadding: number;
        container: false;
        customClass: string;
    } {
        let tooltipText = '';

        if (!this.movedObject) {
            tooltipText += `<b style='font-size:18px'>${this.formatDateToTime(
                eventFlowItem.startedAt,
            )} - ${this.formatDateToTime(eventFlowItem.endedAt)} </b> <br />`;

            if (eventFlowItem.responsible) {
                tooltipText += `<div class="mb-2"><b class='resp-color' style='background-color:${eventFlowItem.responsible.color}'></b>`;
                tooltipText += ` <b>  ${
                    eventFlowItem.responsible?.name ??
                    this.$t('responsible.default')
                }</b></div>`;
            }

            tooltipText += `<b>${this.$t(
                'event-flow.tooltip.total-time',
            )}</b> ${differenceInTime(
                eventFlowItem.endedAt,
                eventFlowItem.startedAt,
            )} <br/>`;

            tooltipText += `<b>${this.$t('event-flow.tooltip.location')}</b> ${
                eventFlowItem.location?.name ?? this.$t('location.default')
            } <br/>`;

            tooltipText += `<b>${this.$t('event-flow.tooltip.topic')}</b> ${
                eventFlowItem.topic?.name ?? this.$t('topic.default')
            } <br/>`;

            if (eventFlowItem.description) {
                tooltipText += ` <b style='margin-top:25px'>${this.$t(
                    'event-flow.tooltip.description',
                )}</b>`;
                tooltipText += `<div class="tooltip-desc">${this.getTooltipDescription(
                    eventFlowItem.description,
                )}</div>`;
            }
        }

        return {
            title: tooltipText,
            disabled: !!this.movedObject,
            html: true,
            trigger: 'hover',
            placement: 'right',
            fallbackPlacement: ['right', 'left', 'top', 'bottom'],
            boundary: 'viewport',
            boundaryPadding: 8,
            container: 'body',
            customClass: 'event-flow-grid-tooltip',
        };
    }

    getTooltipDescription(description: string): string {
        if (description.length <= EventFlowGrid.TOOLTIP_DESCRIPTION_LIMIT) {
            return description;
        }

        return `${description.substring(
            0,
            EventFlowGrid.TOOLTIP_DESCRIPTION_LIMIT,
        )}...`;
    }

    formatDateToTime(date: Date): string {
        return date.toLocaleTimeString(this.$i18n.locale, timeFormatOptions);
    }

    updateCurrentTime(): void {
        this.currentTime = new Date();
    }
}
